import { useState, useCallback } from 'react';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';

interface ReturnedFiltersState {
  events: string[];
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

export const useReturnedFilters = ({
  initialConditions,
  onChange,
}: {
  initialConditions: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}) => {
  const parseConditions = (conditions: FilterConditionOutput[]): ReturnedFiltersState => {
    const state: ReturnedFiltersState = {
      events: [],
      dateFrom: undefined,
      dateTo: undefined,
    };

    conditions.forEach((condition) => {
      if (condition.column === 'event') {
        if (condition.action === 'equals') {
          state.events = [condition.value as string];
        } else if (condition.action === 'in') {
          state.events = condition.value as string[];
        }
      }
      if (condition.column === 'date' && condition.action === 'between') {
        const [from, to] = condition.value as [string, string];
        state.dateFrom = from ? new Date(from) : undefined;
        state.dateTo = to ? new Date(to) : undefined;
      }
    });

    return state;
  };

  const [filters, setFilters] = useState<ReturnedFiltersState>(() => parseConditions(initialConditions));

  const convertToConditions = useCallback((state: ReturnedFiltersState) => {
    const conditions: FilterConditionOutput[] = [];

    if (state.events.length > 0) {
      if (state.events.length === 1) {
        conditions.push({
          column: 'event',
          action: 'equals',
          value: state.events[0],
        });
      } else {
        conditions.push({
          column: 'event',
          action: 'in',
          value: state.events,
        });
      }
    }

    if (state.dateFrom && state.dateTo) {
      conditions.push({
        column: 'date',
        action: 'between',
        value: [state.dateFrom.toISOString(), state.dateTo.toISOString()],
      });
    }

    return conditions;
  }, []);

  const handleFilterChange = (key: keyof ReturnedFiltersState, value: any) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      onChange?.(convertToConditions(next));
      return next;
    });
  };

  const clearFilters = () => {
    const next = {
      events: [],
      dateFrom: undefined,
      dateTo: undefined,
    };
    setFilters(next);
    onChange?.(convertToConditions(next));
  };

  return {
    filters,
    handleFilterChange,
    clearFilters,
  };
};
